import { createGlobalStyle } from "styled-components";

const styled = { createGlobalStyle };

const GlobalStyles = styled.createGlobalStyle`
  :root {
    --font-family-primary: "Plus Jakarta Sans", sans-serif;

    /* ===== Brand (aqua blue) =====
       Two designer-approved anchors — these are the only blues we use:
         ★ MAIN  --color-brand-400: #36AFFD  (sky)
         ★ MAIN  --color-brand-600: #0474FB  (vivid brand)
       The rest is an HSL-interpolated tonal scale derived from those two.
       Do NOT introduce any other blues — always pick the closest step. */
    --color-brand-100: #d7ecfe;
    --color-brand-200: #b5dbfe;
    --color-brand-300: #82c5fe;
    --color-brand-400: #36affd; /* ★ MAIN — sky */
    --color-brand-500: #1b92fc;
    --color-brand-600: #0474fb; /* ★ MAIN — vivid */
    --color-brand-700: #0560d4;
    --color-brand-800: #0848a1;
    --color-brand-900: #0a2f70;
    /* RGB triplets — use with rgba() when you need an alpha-tinted brand
       (shadows, glows, hover washes). Keeps every alpha tied to the same
       anchored hue so we never drift back into stray rgba(N, N, N, A). */
    --color-brand-200-rgb: 181, 219, 254;
    --color-brand-300-rgb: 130, 197, 254;
    --color-brand-400-rgb: 54, 175, 253;
    --color-brand-500-rgb: 27, 146, 252;
    --color-brand-600-rgb: 4, 116, 251;
    --color-brand-700-rgb: 5, 96, 212;
    --color-brand-800-rgb: 8, 72, 161;
    --color-brand-900-rgb: 10, 47, 112;
    --color-brand-glow: rgba(var(--color-brand-400-rgb), 0.28);
    --color-brand-mist: rgba(var(--color-brand-200-rgb), 0.55);

    /* ===== Light neutrals ===== */
    --color-bg-page: #eaf4ff;
    --color-bg-page-alt: #dcecff;
    /* surfaces use the white-rgb triplet so they flip in dark theme */
    --color-surface-1: rgba(var(--glass-white-rgb), 0.72);
    --color-surface-2: rgba(var(--glass-white-rgb), 0.85);
    --color-surface-3: rgba(var(--glass-white-rgb), 0.95);
    --color-surface-input: rgba(var(--glass-white-rgb), 0.7);
    --color-line: rgba(var(--color-brand-300-rgb), 0.22);
    --color-line-strong: rgba(var(--color-brand-400-rgb), 0.45);

    /* ===== Glass tokens (used by liquid-glass styled components) =====
       These replace hardcoded rgba(255, 255, 255, ...) values in styled
       components so the entire frosted-glass system inverts cleanly when
       [data-theme="dark"] is set on <html>.

       The two triplet tokens below are the master switches: any styled
       component that writes rgba(var(--glass-white-rgb), 0.78) or
       rgba(var(--glass-pale-rgb), 0.5) flips automatically in dark mode
       while keeping its original alpha. */
    --glass-white-rgb: 255, 255, 255;
    --glass-pale-rgb: 226, 242, 255;
    --glass-page-tint-rgb: 234, 245, 255;

    --glass-edge-strong: rgba(var(--glass-white-rgb), 0.95);
    --glass-edge: rgba(var(--glass-white-rgb), 0.85);
    --glass-edge-soft: rgba(var(--glass-white-rgb), 0.6);
    --glass-edge-faint: rgba(var(--glass-white-rgb), 0.35);
    --glass-sheen: rgba(var(--glass-white-rgb), 0.85);
    --glass-sheen-soft: rgba(var(--glass-white-rgb), 0.55);
    --glass-base-1: rgba(var(--glass-white-rgb), 0.95);
    --glass-base-2: rgba(var(--glass-white-rgb), 0.78);
    --glass-base-3: rgba(var(--glass-white-rgb), 0.42);
    --glass-tint-1: rgba(var(--glass-page-tint-rgb), 1);
    --glass-tint-2: rgba(214, 234, 255, 1);
    --glass-tint-3: rgba(var(--glass-pale-rgb), 0.6);
    --glass-tint-soft: rgba(232, 244, 255, 0.9);
    --glass-page-grad: linear-gradient(
      180deg,
      #f2faff 0%,
      #dcecff 54%,
      #eaf4ff 100%
    );
    --glass-text-on-pill: #ffffff;

    /* Body radial-wash alphas — dimmed in dark theme so brand washes
       don't read as "light spots" against the navy base. */
    --bg-radial-a-alpha: 0.45;
    --bg-radial-b-alpha: 0.28;
    --bg-radial-c-alpha: 0.38;

    /* Brand-tinted shadow halos that wrap pills, cards and modals. In
       light theme these read as soft aqua glow; in dark theme they
       become low-alpha shadows that only emphasise depth without lighting
       the page up like a beacon. Replace one-off brand-shadow recipes
       with these tokens to get instant theme-aware behaviour. */
    --shadow-halo-strong:
      0 14px 28px rgba(var(--color-brand-600-rgb), 0.38),
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.2);
    --shadow-halo-mid:
      0 10px 22px rgba(var(--color-brand-500-rgb), 0.22),
      0 3px 8px rgba(var(--color-brand-500-rgb), 0.12);
    --shadow-halo-soft:
      0 6px 14px rgba(var(--color-brand-500-rgb), 0.16),
      0 2px 5px rgba(var(--color-brand-500-rgb), 0.08);

    /* ===== Text ===== */
    --color-text-strong: #0f2f54;
    --color-text-body: #1f4675;
    --color-text-muted: #5780af;
    --color-text-dim: #88a5c9;
    --color-text-on-brand: #ffffff;

    /* ===== Radii ===== */
    --radius-sm: 12px;
    --radius-md: 16px;
    --radius-lg: 22px;
    --radius-xl: 28px;
    --radius-pill: 9999px;

    /* ===== Shadows (light + inner gloss = liquid feel) ===== */
    --shadow-micro:
      0 4px 12px rgba(var(--color-brand-700-rgb), 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    --shadow-panel:
      0 24px 48px rgba(var(--color-brand-700-rgb), 0.14),
      0 0 0 1px rgba(var(--color-brand-300-rgb), 0.14),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    --shadow-glow:
      0 0 0 1px rgba(var(--color-brand-400-rgb), 0.18),
      0 0 32px rgba(var(--color-brand-400-rgb), 0.26);
    --shadow-cta:
      0 12px 28px rgba(var(--color-brand-500-rgb), 0.38),
      inset 0 1px 0 rgba(255, 255, 255, 0.55);
    /* Signature "liquid" elevation — use on pills, icon wells, hover cards */
    --shadow-liquid:
      0 18px 36px rgba(var(--color-brand-400-rgb), 0.22),
      inset 0 2px 0 rgba(255, 255, 255, 0.9),
      inset 0 -10px 20px rgba(var(--color-brand-200-rgb), 0.28);

    --ease-snappy: cubic-bezier(0.22, 1, 0.36, 1);
  }

  /* CollectionFilter chip-text token — inline brand-blue in light theme,
     bright text in dark theme. CollectionFilter chips read this so the
     row reads as a unified bright ribbon on dark glass without overriding
     the white text on the active brand-blue selected chip. */
  :root {
    --cf-text: var(--color-brand-700);
  }
  [data-theme="dark"] {
    --cf-text: var(--color-text-strong);
  }

  /* ===== Dark theme override =====
     Activated by [data-theme="dark"] on <html>. Overrides the same token
     set so every component that reads from CSS variables inverts cleanly
     without per-component dark-mode rules. Brand blues stay constant —
     only neutrals, glass surfaces, text and shadows flip. */
  [data-theme="dark"] {
    /* Master tonal triplets — every rgba(var(--glass-white-rgb), X) and
       rgba(var(--glass-pale-rgb), X) in styled components flips here. */
    --glass-white-rgb: 22, 36, 62;
    --glass-pale-rgb: 14, 24, 44;
    --glass-page-tint-rgb: 10, 18, 34;

    /* ===== Dark neutrals ===== */
    --color-bg-page: #0a1424;
    --color-bg-page-alt: #060d1a;
    --color-surface-1: rgba(20, 32, 54, 0.72);
    --color-surface-2: rgba(22, 36, 62, 0.85);
    --color-surface-3: rgba(26, 42, 70, 0.95);
    --color-surface-input: rgba(16, 28, 50, 0.7);
    --color-line: rgba(var(--color-brand-400-rgb), 0.18);
    --color-line-strong: rgba(var(--color-brand-400-rgb), 0.4);

    /* Dark glass tokens — bases flip via the triplet above; the edge /
       sheen tokens go fully transparent in dark theme so the bright
       white outline that wraps frosted-glass pills, modals and the
       header bar disappears entirely on the deep-navy page. */
    --glass-edge-strong: rgba(255, 255, 255, 0);
    --glass-edge: rgba(255, 255, 255, 0);
    --glass-edge-soft: rgba(255, 255, 255, 0);
    --glass-edge-faint: rgba(255, 255, 255, 0);
    --glass-sheen: rgba(255, 255, 255, 0);
    --glass-sheen-soft: rgba(255, 255, 255, 0);
    --glass-tint-2: rgba(8, 16, 32, 1);
    --glass-tint-soft: rgba(14, 24, 44, 0.9);
    --glass-page-grad: linear-gradient(
      180deg,
      #08111f 0%,
      #050b16 54%,
      #0a1424 100%
    );
    --bg-radial-a-alpha: 0.14;
    --bg-radial-b-alpha: 0.1;
    --bg-radial-c-alpha: 0.12;

    /* Dark halo recipes — drop the bright brand glow, keep an ambient
       black drop shadow with a gentle deep-brand inner kiss. */
    --shadow-halo-strong:
      0 14px 28px rgba(0, 0, 0, 0.55), 0 4px 10px rgba(0, 0, 0, 0.32);
    --shadow-halo-mid:
      0 10px 22px rgba(0, 0, 0, 0.5), 0 3px 8px rgba(0, 0, 0, 0.28);
    --shadow-halo-soft:
      0 6px 14px rgba(0, 0, 0, 0.45), 0 2px 5px rgba(0, 0, 0, 0.22);

    /* ===== Dark text ===== */
    --color-text-strong: #eaf4ff;
    --color-text-body: #c9dcf3;
    --color-text-muted: #8aa3c4;
    --color-text-dim: #5e7ba0;
    --color-text-on-brand: #ffffff;

    /* ===== Dark shadows ===== */
    --shadow-micro:
      0 4px 12px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    --shadow-panel:
      0 24px 48px rgba(0, 0, 0, 0.6),
      0 0 0 1px rgba(var(--color-brand-400-rgb), 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
    --shadow-glow:
      0 0 0 1px rgba(var(--color-brand-400-rgb), 0.32),
      0 0 36px rgba(var(--color-brand-400-rgb), 0.36);
    --shadow-cta:
      0 12px 28px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.18);
    --shadow-liquid:
      0 18px 36px rgba(0, 0, 0, 0.55), inset 0 2px 0 rgba(255, 255, 255, 0.08),
      inset 0 -10px 20px rgba(var(--color-brand-700-rgb), 0.32);
  }

  *,
  *::after,
  *::before {
    box-sizing: inherit;
    margin: 0;
    padding: 0;
    scroll-behavior: smooth;
  }

  /* Firefox */
  * {
    scrollbar-width: auto;
    scrollbar-color: transparent;
    -webkit-user-select: none; /* Safari */
    -ms-user-select: none; /* IE 10+ */
    user-select: none; /* Standard */
    -webkit-tap-highlight-color: transparent;
  }

  /* Chrome, Edge, and Safari */
  *::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }

  *::-webkit-scrollbar-track {
    background: transparent;
  }

  *::-webkit-scrollbar-thumb {
    background-color: transparent;
  }

  html,
  body {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  html.feed-scroll-mode,
  body.feed-scroll-mode {
    overflow: hidden;
    height: 100dvh;
    overscroll-behavior: contain;
  }

  html {
    height: 100%;
    font-size: 62.5%; // 1rem = 10px, 10/16 = 60.25%
    box-sizing: border-box;
    font-family: var(--font-family-primary);
    overflow-x: hidden;
    user-select: none;
    background: var(--color-bg-page);
    color: var(--color-text-body);

    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    font-feature-settings: "kern" 1;
  }

  body {
    box-sizing: border-box;
    min-height: 100%;
    position: relative;
    overflow-x: hidden;
    /* Soft aqua wash: pale blue-white with radial aqua highlights.
       Radial alphas use tokens so dark theme can dim them — bright
       brand washes on a deep-navy base would otherwise still read
       "light." */
    background:
      radial-gradient(
        circle at 12% 16%,
        rgba(var(--color-brand-300-rgb), var(--bg-radial-a-alpha)),
        transparent 0 32%
      ),
      radial-gradient(
        circle at 88% 10%,
        rgba(var(--color-brand-400-rgb), var(--bg-radial-b-alpha)),
        transparent 0 28%
      ),
      radial-gradient(
        circle at 50% 92%,
        rgba(var(--color-brand-200-rgb), var(--bg-radial-c-alpha)),
        transparent 0 34%
      ),
      var(--glass-page-grad);
    color: var(--color-text-body);
    font-family: var(--font-family-primary);

    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Ambient water-drop decorations running down each page edge */
  body::before,
  body::after {
    content: "";
    position: fixed;
    top: 0;
    bottom: 0;
    width: 180px;
    pointer-events: none;
    z-index: 0;
    filter: drop-shadow(0 6px 18px rgba(var(--color-brand-400-rgb), 0.24));
    background-repeat: no-repeat, no-repeat, no-repeat;
  }

  @keyframes fapifyDriftA {
    0% {
      transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(8px, 16px, 0);
    }
  }

  @keyframes fapifyDriftB {
    0% {
      transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(-8px, -18px, 0);
    }
  }

  /* Narrower screens — scale decor down, soften opacity so UI stays clear */
  @media (max-width: 768px) {
    body::before,
    body::after {
      width: 100px;
      opacity: 0.36;
    }

    body::before {
      left: -18px;
      background-size:
        78px auto,
        56px auto,
        120px auto;
      background-position:
        0 8vh,
        10px 44vh,
        -8px calc(100vh - 90px);
    }

    body::after {
      right: -18px;
      background-size:
        70px auto,
        100px auto,
        128px auto;
      background-position:
        right 4px top 10vh,
        right 8px top 44vh,
        right -12px bottom 44px;
    }
  }

  @media (max-width: 480px) {
    body::before,
    body::after {
      opacity: 0.28;
    }
  }

  #root {
    position: relative;
    min-height: 100dvh;
    isolation: isolate;
    z-index: 1;
  }

  /* Skeleton placeholders tokom loading-a */
  img[loading="lazy"]:not([src]) {
    background: linear-gradient(
      90deg,
      rgba(var(--color-brand-200-rgb), 0.18) 25%,
      rgba(var(--color-brand-200-rgb), 0.32) 50%,
      rgba(var(--color-brand-200-rgb), 0.18) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  input,
  button,
  select,
  textarea {
    outline: none;
    font: inherit;
  }

  button {
    color: inherit;
  }

  input,
  select,
  textarea {
    color: var(--color-text-strong);
    caret-color: var(--color-brand-600);
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--color-text-dim);
  }

  a {
    color: var(--color-brand-600);
    transition:
      color 180ms var(--ease-snappy),
      opacity 180ms var(--ease-snappy);
  }

  a:hover {
    color: var(--color-brand-700);
  }

  ::selection {
    background: rgba(var(--color-brand-400-rgb), 0.28);
    color: var(--color-text-strong);
  }

  :focus-visible {
    outline: none;
    box-shadow: none;
  }

  #cf-turnstile {
    width: 100% !important;
    max-width: 100%;
    display: block;
  }
  #cf-turnstile > div {
    width: 100% !important;
    display: flex;
    justify-content: center;
  }

  .tg-auth-button {
    height: 42px !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
    text-align: center !important;
    font-family: var(--font-family-primary) !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    letter-spacing: -0.21px !important;
    border-radius: 100px !important;
    border: 1px solid rgba(255, 255, 255, 0.6) !important;
    background: linear-gradient(
      180deg,
      var(--color-brand-300) 0%,
      var(--color-brand-500) 100%
    ) !important;
    color: #ffffff !important;
    cursor: pointer;
    box-shadow: var(--shadow-cta);
    transition:
      background 0.2s ease,
      box-shadow 0.2s ease,
      transform 0.2s ease;

    &:hover {
      background: linear-gradient(180deg, #4fb4ff 0%, #146cd0 100%) !important;
      box-shadow: var(--shadow-glow);
      transform: translateY(-1px);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  li {
    font-size: 14px;
  }

  @media (max-width: 400px) {
    #cf-turnstile {
      scale: 0.925;
    }
    #cf-turnstile > div {
      scale: 0.925;
    }
  }

  /* ─── Mobile / tablet performance guard ─────────────────────────────────
     Fixed-position water-drop decor with drop-shadow filter adds an extra
     full-viewport raster pass per scroll frame. On phones / low-power
     laptops it can't keep up — drop the filter below 1024px. */
  @media (max-width: 1024px) {
    body::before,
    body::after {
      filter: none !important;
    }
  }

  /* ─── Sub-448px fake blur ────────────────────────────────────────────────
     On very narrow mobile screens, replace runtime backdrop blurs with
     light-theme frosted surfaces: higher alpha, soft aqua tint, stronger
     edge definition, and no backdrop sampling. */
  @media (max-width: 447.98px) {
    :root {
      --color-surface-1: rgba(var(--glass-white-rgb), 0.92);
      --color-surface-2: rgba(var(--glass-white-rgb), 0.96);
      --color-surface-3: rgba(var(--glass-white-rgb), 0.985);
      --color-surface-input: rgba(var(--glass-white-rgb), 0.94);
      --color-line: rgba(var(--color-brand-300-rgb), 0.3);
      --color-line-strong: rgba(var(--color-brand-400-rgb), 0.5);
      --shadow-panel:
        0 18px 36px rgba(var(--color-brand-700-rgb), 0.13),
        0 0 0 1px rgba(var(--color-brand-300-rgb), 0.18),
        inset 0 1px 0 rgba(255, 255, 255, 0.98),
        inset 0 -18px 36px rgba(var(--color-brand-200-rgb), 0.14);
      --shadow-liquid:
        0 14px 28px rgba(var(--color-brand-400-rgb), 0.18),
        inset 0 2px 0 rgba(255, 255, 255, 0.98),
        inset 0 -10px 20px rgba(var(--color-brand-200-rgb), 0.22);
    }

    [data-theme="dark"] {
      --color-line: rgba(var(--color-brand-400-rgb), 0.26);
      --color-line-strong: rgba(var(--color-brand-400-rgb), 0.48);
      --shadow-panel:
        0 18px 36px rgba(0, 0, 0, 0.7),
        0 0 0 1px rgba(var(--color-brand-400-rgb), 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        inset 0 -18px 36px rgba(var(--color-brand-700-rgb), 0.22);
      --shadow-liquid:
        0 14px 28px rgba(0, 0, 0, 0.55),
        inset 0 2px 0 rgba(255, 255, 255, 0.08),
        inset 0 -10px 20px rgba(var(--color-brand-700-rgb), 0.28);
    }

    *,
    *::before,
    *::after {
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }
  }
`;

export default GlobalStyles;
